import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import api from "../utils/api";
import ReviewsBar from "../components/ReviewsBar";
import LiveStrip from "../components/LiveStrip";
import "../styles/parkingVsTaxi.css";

const isValidPostcode = (pc) => /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i.test(pc.trim());
const money = (v) => (v == null || Number.isNaN(Number(v)) ? "—" : Number(v).toFixed(2));

export default function ParkingVsTaxi7days() {
  const [form, setForm] = useState({
    pickup_postcode: "",
    dropoff_terminal: "LHR5",
    passengers: 4,
    luggage: 3,
    days_parked: 7,
    pickup_datetime: ""
  });

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const autoSubmitted = useRef(false);

  useEffect(() => {
    const nextMondayAt11 = () => {
      const now = new Date();
      const day = now.getDay();
      const daysToMon = (8 - day) % 7 || 7;
      const target = new Date(now);
      target.setDate(now.getDate() + daysToMon);
      target.setHours(11, 0, 0, 0);
      const pad = (n) => String(n).padStart(2, "0");
      return `${target.getFullYear()}-${pad(target.getMonth() + 1)}-${pad(target.getDate())}T${pad(target.getHours())}:${pad(target.getMinutes())}`;
    };

    setForm((prev) => ({
      ...prev,
      pickup_postcode: "SW1A 1AA", // neutral London example
      dropoff_terminal: "LHR5",
      passengers: 4,
      luggage: 3,
      days_parked: 7,
      pickup_datetime: nextMondayAt11()
    }));
  }, []);

  const submit = async () => {
    if (!form.pickup_postcode.trim() || !isValidPostcode(form.pickup_postcode)) {
      setError("Please enter a valid UK pickup postcode.");
      return;
    }
    if (!form.pickup_datetime) {
      setError("Please select a pickup date and time.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        ...form,
        passengers: Math.min(6, Math.max(1, Number(form.passengers) || 4)),
        luggage: Math.min(6, Math.max(0, Number(form.luggage) || 3)),
        days_parked: 7 // keep comparison anchored to 7 days
      };
      const res = await api.post("/api/parking/compare", payload);
      setResult(res.data);
    } catch (err) {
      setError("Failed to calculate comparison. Please try again.");
    }

    setLoading(false);
  };

  useEffect(() => {
    if (autoSubmitted.current) return;
    if (form.pickup_postcode && form.pickup_datetime) {
      autoSubmitted.current = true;
      submit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.pickup_postcode, form.pickup_datetime]);

  const bestKey = result?.recommendation?.best_value;
  const parkingTotal = result?.costs?.parking?.total ?? null;
  const taxiTwoWay = result?.costs?.taxi?.cheapcabgrab_two_way ?? null;
  const delta = parkingTotal != null && taxiTwoWay != null ? Number((parkingTotal - taxiTwoWay).toFixed(2)) : null;

  const heroVerdict = useMemo(() => {
    if (delta == null) return "Compare complete.";
    if (Math.abs(delta) < 5) return "Costs are similar for a 7-day trip — choose based on fatigue and convenience.";
    if (delta < 0) return `✅ Best choice for this 7-day trip: Heathrow Parking (about £${money(Math.abs(delta))} cheaper than two taxis).`;
    return `🚖 Best choice for this 7-day trip: Taxi (about £${money(Math.abs(delta))} cheaper than parking once fuel/time are included).`;
  }, [delta]);

  const sectionTitleStyle = { marginTop: 20, marginBottom: 8, fontSize: 18, fontWeight: 800, color: "#0f172a" };
  const listStyle = { paddingLeft: 18, lineHeight: 1.7, color: "#1f2937", fontSize: 15, marginTop: 6, marginBottom: 6 };
  const bodyTextStyle = { fontSize: 15, color: "#1f2937", lineHeight: 1.6, marginTop: 4, marginBottom: 4 };

  return (
    <div className="parking-wrapper">
      <Helmet>
        <title>7 Days Heathrow Parking vs Taxi (2026): Which Is Cheaper?</title>
        <meta
          name="description"
          content="Compare the real cost of 7 days Heathrow parking vs two taxi trips. Includes fuel, hassle, time, and live taxi pricing — updated for 2026."
        />
        <link rel="canonical" href="https://www.cheapcabgrab.com/7-day-heathrow-parking-vs-taxi" />
        <meta property="og:title" content="7 Days Heathrow Parking vs Taxi (2026): Which Is Cheaper?" />
        <meta
          property="og:description"
          content="See whether parking for 7 days or booking two taxis is cheaper for your Heathrow trip. Fuel, time, hassle, and live fares included."
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "Is 7 days parking cheaper than a taxi to Heathrow?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Often yes for longer drives; this calculator compares 7 days of parking vs two taxi trips for your postcode and terminal."
                }
              },
              {
                "@type": "Question",
                name: "Does the 7-day parking comparison include fuel costs?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Yes. Parking includes fuel for both journeys plus time/convenience. Taxi includes two trips with live pricing ranges."
                }
              },
              {
                "@type": "Question",
                name: "Is Long Stay parking safe for a week?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Official Heathrow Long Stay is commonly used for week-long trips. Always book officially for security and reliability."
                }
              },
              {
                "@type": "Question",
                name: "What if my return flight is delayed?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Delays add to parking time; taxis avoid extra parking time but can be pricier. Re-run the calculator if your timings change."
                }
              },
              {
                "@type": "Question",
                name: "Is a taxi better for families after a long holiday?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Many families prefer taxis to avoid post-flight driving fatigue. The calculator shows the price trade-off vs 7-day parking."
                }
              },
              {
                "@type": "Question",
                name: "How far is Long Stay from the terminal?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Expect a short shuttle ride from Long Stay to your terminal. Factor this into your arrival time."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      <LiveStrip variant="heathrow" />

      <div className="parking-header">
        <h1>7 Days Heathrow Parking vs Taxi (2026 Guide)</h1>
        <p style={{ fontSize: 15, color: "#0f172a", marginBottom: 6 }}>
          Example calculation for a 7-day holiday. Change postcode or terminal to see your exact numbers.
        </p>
        <p style={{ fontSize: 14, color: "#374151", marginBottom: 0 }}>
          Need the full tool? <a href="/heathrow-parking-vs-taxi">Open the main parking vs taxi calculator.</a>
        </p>
      </div>

      <div style={{ marginTop: 12, border: "1px solid #e5e7eb", borderRadius: 12, padding: 12, background: "#f8fafc" }}>
        <div style={{ fontWeight: 800, fontSize: 18 }}>The quick answer for a 7-day trip</div>
        <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", marginTop: 8 }}>
          <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 10, background: "#fff" }}>
            <div style={{ fontWeight: 800 }}>Use PARKING if:</div>
            <ul style={listStyle}>
              <li>You’ll be away for a full week.</li>
              <li>You prefer driving your own car home.</li>
              <li>Your pickup postcode is far from Heathrow.</li>
            </ul>
          </div>
          <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 10, background: "#fff" }}>
            <div style={{ fontWeight: 800 }}>Use TAXI if:</div>
            <ul style={listStyle}>
              <li>You want zero post-flight driving fatigue.</li>
              <li>Your postcode is close to Heathrow.</li>
              <li>You value door-to-door over parking shuttles.</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="parking-form" style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, marginTop: 14 }}>
        <div className="form-group">
          <label>Pickup postcode (edit for your trip)</label>
          <input
            className="parking-input"
            value={form.pickup_postcode}
            onChange={(e) => setForm({ ...form, pickup_postcode: e.target.value })}
            placeholder="e.g. SW1A 1AA"
          />
        </div>

        <div className="form-group">
          <label>Terminal</label>
          <select
            className="parking-input"
            value={form.dropoff_terminal}
            onChange={(e) => setForm({ ...form, dropoff_terminal: e.target.value })}
          >
            <option value="LHR2">Terminal 2</option>
            <option value="LHR3">Terminal 3</option>
            <option value="LHR4">Terminal 4</option>
            <option value="LHR5">Terminal 5</option>
          </select>
        </div>

        <div className="form-group">
          <label>Passengers</label>
          <input
            type="number"
            className="parking-input"
            min="1"
            max="6"
            value={form.passengers}
            onChange={(e) => setForm({ ...form, passengers: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>Luggage</label>
          <input
            type="number"
            className="parking-input"
            min="0"
            max="6"
            value={form.luggage}
            onChange={(e) => setForm({ ...form, luggage: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>Days parked</label>
          <input className="parking-input" value="7 (locked for this comparison)" disabled />
        </div>

        <div className="form-group">
          <label>Pickup date & time</label>
          <input
            type="datetime-local"
            className="parking-input"
            value={form.pickup_datetime}
            onChange={(e) => setForm({ ...form, pickup_datetime: e.target.value })}
          />
        </div>

        <button className="parking-btn" onClick={submit}>
          {loading ? "Calculating…" : "Compare parking vs taxi for 7 days"}
        </button>

        {error && <p style={{ color: "red", marginTop: 8 }}>{error}</p>}
      </div>

      {result && (
        <div className="parking-results">
          <div
            style={{
              border: "1px solid #16a34a",
              borderRadius: 14,
              padding: 16,
              background: "#ecfdf5",
              boxShadow: "0 8px 20px rgba(22,163,74,0.08)"
            }}
          >
            <div style={{ fontWeight: 800, fontSize: 18 }}>{heroVerdict}</div>
            <div style={{ marginTop: 6, fontSize: 14, color: "#1f2937" }}>
              Assumes 7 full days parked (Long Stay) vs two taxi journeys. Fuel, time, and hassle scores are included.
            </div>
            <div style={{ marginTop: 6, fontSize: 12, color: "#475467" }}>
              Results vary by distance, passengers, and return-journey fatigue.
            </div>
            <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px,1fr))", gap: 8 }}>
              <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 10, background: "#fff" }}>
                <div style={{ fontWeight: 700 }}>Parking total (7 days)</div>
                <div style={{ fontSize: 18 }}>{money(parkingTotal)}</div>
              </div>
              <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 10, background: "#fff" }}>
                <div style={{ fontWeight: 700 }}>Two taxis (return)</div>
                <div style={{ fontSize: 18 }}>{money(taxiTwoWay)}</div>
              </div>
            </div>
            <div style={{ marginTop: 8, fontSize: 12, color: "#6b7280" }}>
              Postcode: {result.journey?.pickup_postcode} • Terminal: {result.costs?.parking?.terminal || result.journey?.dropoff_terminal} • Passengers: {result.journey?.passengers} • Luggage: {result.journey?.luggage}
            </div>
          </div>

          <div style={{ marginTop: 14, border: "1px solid #e5e7eb", borderRadius: 12, padding: 14, background: "#fff" }}>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>Cost comparison: parking vs two taxi journeys</div>
            <table className="parking-table" style={{ marginTop: 8 }}>
              <thead>
                <tr>
                  <th>Option</th>
                  <th>Cost (£)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>🚗 Parking total (7 days)</td>
                  <td>{money(result.costs?.parking?.total)}</td>
                </tr>
                <tr>
                  <td style={{ paddingLeft: 24, color: "#666" }}>└ Parking price ({result.costs?.parking?.pricing_mode || "—"})</td>
                  <td style={{ color: "#666" }}>{money(result.costs?.parking?.parking_price)}</td>
                </tr>
                <tr>
                  <td style={{ paddingLeft: 24, color: "#666" }}>└ Fuel estimate (return driving)</td>
                  <td style={{ color: "#666" }}>{money(result.costs?.parking?.fuel_price)}</td>
                </tr>
                <tr>
                  <td>🚖 CheapCabGrab (2 trips)</td>
                  <td>{money(result.costs?.taxi?.cheapcabgrab_two_way)}</td>
                </tr>
                <tr>
                  <td>📱 Ride-hail range (2 trips)</td>
                  <td>
                    {result.costs?.taxi?.ride_hail_two_way
                      ? `£${money(result.costs.taxi.ride_hail_two_way.min)} – £${money(result.costs.taxi.ride_hail_two_way.max)}`
                      : "—"}
                  </td>
                </tr>
                <tr>
                  <td>🚕 Black cab range (2 trips)</td>
                  <td>
                    {result.costs?.taxi?.black_cab_two_way_range
                      ? `£${money(result.costs.taxi.black_cab_two_way_range.min)} – £${money(result.costs.taxi.black_cab_two_way_range.max)}`
                      : result.costs?.taxi?.black_cab_two_way != null
                        ? `~£${money(result.costs.taxi.black_cab_two_way)}`
                        : "—"}
                  </td>
                </tr>
              </tbody>
            </table>
            <div style={{ marginTop: 8, fontSize: 13, color: "#374151" }}>
              Need other options? <a href="/heathrow-parking-vs-taxi">Open the main parking vs taxi tool</a> or check <a href="/heathrow-dropoff-free">free drop-off options</a>.
            </div>
          </div>

          <div style={{ marginTop: 12, border: "1px solid #e5e7eb", borderRadius: 12, padding: 14, background: "#f9fafb" }}>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>What most people forget on a 7-day trip</div>
            <ul style={listStyle}>
              <li>Driving home after a week away with jet lag and luggage.</li>
              <li>Children or elderly passengers increase hassle for parking shuttles.</li>
              <li>Return journey fatigue — taxis remove post-flight driving.</li>
            </ul>
          </div>

          <div style={{ marginTop: 12, border: "1px solid #e5e7eb", borderRadius: 12, padding: 14, background: "#fff" }}>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>When parking wins for a 7-day holiday</div>
            <ul style={listStyle}>
              <li>Longer drives (e.g., Reading, Oxford, outside M25).</li>
              <li>You prefer your own car and flexible timing.</li>
              <li>You’re price-sensitive and comfortable driving home after a long flight.</li>
            </ul>

            <div style={{ fontWeight: 800, margin: "14px 0 6px" }}>When a taxi is actually cheaper</div>
            <ul style={listStyle}>
              <li>Close-in postcodes (SL, TW, UB) with low mileage.</li>
              <li>You want zero fatigue and door-to-door convenience.</li>
              <li>Flight delays expected — no extra parking time to pay.</li>
            </ul>
          </div>

          <div style={{ marginTop: 12, border: "1px solid #e5e7eb", borderRadius: 12, padding: 14, background: "#f8fafc" }}>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>Real examples (re-run with your postcode)</div>
            <ul style={listStyle}>
              <li>Reading → T5: Parking often wins for 7 days; taxi can win if family fatigue is high.</li>
              <li>Central London (SW1A) → T5: Taxi often competitive; parking adds Long Stay + fuel.</li>
              <li>Oxford → T3/T5: Parking usually cheaper, but taxi reduces post-flight driving risk.</li>
            </ul>
          </div>

          <div style={{ marginTop: 12, border: "1px solid #e5e7eb", borderRadius: 12, padding: 14, background: "#fff" }}>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>Use the calculator for your exact trip</div>
            <p style={bodyTextStyle}>
              This page anchors on 7 full days. Change the postcode, terminal, or date/time above to personalise. For non-7-day trips,{" "}
              <a href="/heathrow-parking-vs-taxi">use the main calculator</a>.
            </p>
            <p style={bodyTextStyle}>
              Looking for free drop-off guidance? <a href="/heathrow-dropoff-free-2026">See the free drop-off guide</a>.
            </p>
          </div>

          <div style={{ marginTop: 12, border: "1px solid #e5e7eb", borderRadius: 12, padding: 14, background: "#fff" }}>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>FAQs</div>
            <div style={{ display: "grid", gap: 8 }}>
              {[
                { q: "Is 7 days parking cheaper than a taxi to Heathrow?", a: "Often for longer drives; this calculator shows the saving vs two taxi trips for your postcode." },
                { q: "Does the 7-day comparison include fuel?", a: "Yes. Parking totals include fuel for both journeys plus a time/convenience factor." },
                { q: "Is Long Stay parking safe for a week?", a: "Official Heathrow Long Stay is widely used for week-long trips. Book directly for security and reliability." },
                { q: "What if my return flight is delayed?", a: "Delays extend parking time. Taxis avoid extra parking time but may cost more overall." },
                { q: "Is a taxi better for families after a long holiday?", a: "Taxis remove post-flight driving; the tool shows the price trade-off vs parking for 7 days." },
                { q: "How far is Long Stay from the terminal?", a: "Expect a shuttle ride; allow extra time from Long Stay to your terminal." }
              ].map(({ q, a }) => (
                <details key={q} className="train-collapsible" open={false}>
                  <summary className="train-summary-bar" style={{ fontWeight: 700, fontSize: 15 }}>{q}</summary>
                  <div className="train-collapsible-body" style={{ fontSize: 14, color: "#1f2937" }}>{a}</div>
                </details>
              ))}
            </div>
          </div>

          <div style={{ marginTop: 10, fontSize: 12, color: "#6b7280" }}>
            Last updated: {new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
          </div>
        </div>
      )}

      <div style={{ marginTop: 30 }}>
        <ReviewsBar />
      </div>
    </div>
  );
}
